// Explicit "Find All References in Tab" editor action.
//
// Monaco routes Peek References / Go to References / Find All References
// through ONE `ReferenceProvider`, and since the 2026-08-23 cross-object-peek
// revision that provider returns real locations instead of opening the
// `ReferencesTab` as a side-effect. The grouped tab — with its per-object
// sections, snippets and non-parseable-body rows — is still the better surface
// for a wide audit, so it gets its own editor-context-menu entry alongside
// Monaco's built-in reference commands.
//
// Object-Explorer context menus (`tableContextMenuService` /
// `viewContextMenuService` / `routineContextMenuService` /
// `columnContextMenuService`) keep calling `openReferencesTab` directly and are
// unaffected.
//
// COLD CASE POLICY. The cursor resolvers walk the OE tree synchronously and
// every name comparison in that walk asks how the server tells `dbo` from
// `DBO` (`go-to/oeTreeQualification.ts`). On a connection nothing has probed
// yet the walk falls back to case-INSENSITIVE, so in a `_CS_` database a typed
// `B.dbo.users` is satisfied by `B.DBO.users` as well — the tab would then be
// titled after, and filled with, another object's references. So the target is
// RE-RESOLVED once the policy is known, before the tab is created.
//
// The gate is the POLICY only (`casePolicyReadyFor`), not `indexReadyFor`:
// the tab renders its own loading / index-unavailable states and does its own
// index wait inside `openReferencesTab`, so holding tab CREATION behind a full
// catalog build would only delay the surface that exists to show progress. Peek
// (`referenceProvider.ts`) re-resolves behind the index wait it already makes,
// because it has no loading affordance of its own.

import type * as monacoNs from 'monaco-editor';

import { casePolicyReadyFor } from '../connection-lifecycle/navigationalLifecycle';

import { openReferencesTab } from './openReferencesTab';
import {
  captureContext,
  contextHeldStill,
  resolveBehindPolicyWait,
  type InvocationContext,
  type VersionedModel,
} from './invocationContext';
import {
  findReferenceTargetAtCursor,
  type CursorModel,
  type ReferenceProviderDeps,
  type ReferenceTarget,
} from './referenceTargetAtCursor';

export type FindReferencesInTabDeps = ReferenceProviderDeps & {
  /** Wait until the CASE POLICY of the database the cursor named is known —
   *  the gate the lifecycle opens before it keys anything, without the catalog
   *  build behind it. Defaults to `casePolicyReadyFor`. */
  readonly awaitCasePolicy?: (connectionId: string, database: string) => Promise<void>;
};

export const FIND_REFERENCES_IN_TAB_ACTION_ID = 'jamsql.findReferencesInTab';

export const FIND_REFERENCES_IN_TAB_LABEL = 'Find All References in Tab';

type ActionEditor = {
  readonly getModel: () => (monacoNs.editor.ITextModel | null) | undefined;
  readonly getPosition: () => monacoNs.Position | null;
};

/** What the cursor resolver reads, plus the version the abort guard compares. */
type ActionModel = CursorModel & VersionedModel;

/** Monaco `IActionDescriptor` for the editor context menu. Registered next to
 *  the ReferenceProvider in `SqlMonaco.tsx`. */
export function createFindReferencesInTabAction(
  deps: FindReferencesInTabDeps,
): monacoNs.editor.IActionDescriptor {
  return {
    id: FIND_REFERENCES_IN_TAB_ACTION_ID,
    label: FIND_REFERENCES_IN_TAB_LABEL,
    contextMenuGroupId: 'navigation',
    contextMenuOrder: 1.5,
    run: (editor) => {
      runFindReferencesInTab(deps, editor as unknown as ActionEditor);
    },
  };
}

/** Exported for unit tests + the E2E seam: resolve the cursor target and open
 *  the grouped references tab. Returns `false` when the cursor is not on
 *  anything find-references understands.
 *
 *  `true` says the cursor named something and a tab is on its way — it opens
 *  once the case policy is in (a microtask or two later, see
 *  {@link openOncePolicyKnown}). Several cases open no tab: a typed spelling
 *  the learnt policy rules out — precisely the search that must not be answered
 *  with another object's references — an invocation context that moved under
 *  the wait, a gate that refused for a reason which leaves the binding
 *  unverified, and a database that never settles. */
export function runFindReferencesInTab(
  deps: FindReferencesInTabDeps,
  editor: ActionEditor,
): boolean {
  const model: ActionModel | null | undefined = editor.getModel?.();
  const position = editor.getPosition?.();
  if (!model || !position) return false;

  const target = findReferenceTargetAtCursor(deps, model, position);
  if (!target) return false;

  void openOncePolicyKnown(deps, model, position, captureContext(deps, model, target.connectionId), target);
  return true;
}

/** Re-resolve the cursor once the target database's CASE POLICY is known, then
 *  open the tab for whatever it binds now — see the COLD CASE POLICY note at
 *  the top of this file for why the cold binding cannot be trusted.
 *
 *  The cold target is dropped rather than compared: the second walk is the one
 *  made with the server's real name-comparison rule, so whatever it binds is
 *  the object the user named. `null` means the typed spelling names nothing in
 *  this catalog — no tab, the same answer a cursor that never bound gets.
 *
 *  WHAT A REFUSED GATE MEANS depends on WHY it refused, identically across the
 *  three cursor surfaces (`invocationContext.ts` holds the table and the
 *  argument). `'no-lifecycle'` — the mock engine, an engine the app does not
 *  index, a connection that left the store — proves there is no policy to learn
 *  and no index behind it either: the cursor's own binding stands and the tab
 *  renders the honest EMPTY state (`openReferencesTab` answers `'no-lifecycle'`
 *  from no index; only the other reasons reach its `error` state).
 *  Every other reason leaves the binding UNVERIFIED on a connection that does
 *  have a policy, and the tab would be titled after, and filled with, the
 *  case-different neighbour's references — so no tab opens.
 *
 *  A DATABASE the re-resolution MOVES to is re-verified rather than trusted:
 *  its own collation is what the first wait never probed, so the loop waits for
 *  it and walks the cursor again, bounded.
 *
 *  ABORTING when the INVOCATION CONTEXT moved — the buffer, the connection or
 *  the database — for the reasons `invocationContext.ts` records. No tab opens:
 *  the same answer a cursor that never bound gets, and one click away from a
 *  retry. */
async function openOncePolicyKnown(
  deps: FindReferencesInTabDeps,
  model: ActionModel,
  position: monacoNs.Position,
  captured: InvocationContext,
  cursorTarget: ReferenceTarget,
): Promise<void> {
  const awaitPolicy = deps.awaitCasePolicy ?? casePolicyReadyFor;
  const resolution = await resolveBehindPolicyWait<ReferenceTarget>({
    cursorTarget,
    awaitPolicy: (t) => awaitPolicy(t.connectionId, t.database),
    resolve: () => findReferenceTargetAtCursor(deps, model, position),
    heldStill: () => contextHeldStill(deps, model, captured),
  });
  if (resolution.kind === 'abort') return;
  const target = resolution.target;

  openReferencesTab({
    connectionId: target.connectionId,
    database: target.database,
    engineId: deps.engineId,
    identifier: target.identifier,
    identifierKind: target.identifierKind,
    relation: target.relation,
    trigger: 'editor-action',
  });
}
